import { logError } from './errorLogger';

interface FetchApiOptions extends RequestInit {
  skipErrorLogging?: boolean;
}

export async function fetchApi<T = any>(
  url: string,
  options: FetchApiOptions = {}
): Promise<T> {
  const { skipErrorLogging = false, ...fetchOptions } = options;

  const startTime = performance.now();
  const requestTimestamp = new Date().toISOString();

  try {
    const response = await fetch(url, fetchOptions);
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Clone response to read body for logging
    const responseClone = response.clone();
    let responseBody: any;

    try {
      responseBody = await responseClone.json();
    } catch {
      responseBody = await responseClone.text();
    }

    // Log errors for non-2xx responses
    if (!response.ok && !skipErrorLogging) {
      await logError({
        endpoint: url,
        method: fetchOptions.method || 'GET',
        statusCode: response.status,
        requestBody: fetchOptions.body ? JSON.parse(fetchOptions.body as string) : undefined,
        requestHeaders: fetchOptions.headers,
        responseBody,
        responseHeaders: Object.fromEntries(response.headers.entries()),
        errorType: 'server',
        errorMessage: `HTTP ${response.status}: ${response.statusText}`,
        requestDuration: duration,
        timestampClient: requestTimestamp,
      });
    }

    // Return parsed response
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      (error as any).status = response.status;
      (error as any).data = responseBody;
      throw error;
    }

    return responseBody as T;
  } catch (error: any) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Log network/client errors
    if (!skipErrorLogging) {
      await logError({
        endpoint: url,
        method: fetchOptions.method || 'GET',
        statusCode: 0,
        requestBody: fetchOptions.body ? JSON.parse(fetchOptions.body as string) : undefined,
        requestHeaders: fetchOptions.headers,
        errorType: 'network',
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        requestDuration: duration,
        timestampClient: requestTimestamp,
      });
    }

    throw error;
  }
}
